/**
 * `useRouter` — the shell's history-backed navigation hook (E10-F01-T03).
 *
 * Replaces the legacy hand-rolled `navigate()` / `popstate` wiring in `core.js`
 * with a React hook over the same contract:
 *   - `navigate(route)` → `history.pushState(state, '', path)` + `route.save`
 *     (localStorage) + updates `document.title`; the active view drives the
 *     header page-title and the `#view-*` toggling in `AppShell`.
 *   - the browser back/forward button fires `popstate`, from which the encoded
 *     view (projects/inbox/users/project) is restored.
 *
 * The pure routing model lives in `./routes.ts`; this hook only owns the
 * imperative `history` / DOM-title side effects and the React state.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  pathToRoute,
  routeToPath,
  routeToState,
  stateToRoute,
  titleForView,
  type Route,
} from './routes.js';
import { loadRoute, saveRoute } from './storage.js';

const TITLE_SUFFIX = 'EER Global';

function applyDocumentTitle(route: Route): void {
  if (typeof document !== 'undefined') {
    document.title = `${titleForView(route.view)} · ${TITLE_SUFFIX}`;
  }
}

/**
 * Resolve the initial route on a cold load:
 *   1. an explicit non-root URL wins (deep-link / refresh on a real path);
 *   2. else the persisted route from `route.save`;
 *   3. else the default (projects).
 */
function resolveInitialRoute(): Route {
  if (typeof window === 'undefined') {
    return pathToRoute('/');
  }
  const { pathname } = window.location;
  if (pathname && pathname !== '/') {
    return pathToRoute(pathname);
  }
  return loadRoute() ?? pathToRoute('/');
}

export interface RouterApi {
  /** The currently active route (drives view toggling + page-title). */
  readonly route: Route;
  /** Navigate to a route: pushState + persist + title. No-op if unchanged. */
  navigate: (route: Route) => void;
}

export function useRouter(): RouterApi {
  const [route, setRoute] = useState<Route>(resolveInitialRoute);

  // Establish the initial history entry + title so the first Back is correct
  // and a refresh on a deep link keeps the URL stable.
  useEffect(() => {
    applyDocumentTitle(route);
    if (typeof window !== 'undefined') {
      window.history.replaceState(routeToState(route), '', routeToPath(route));
    }
    // Mount-only: subsequent updates flow through `navigate` / `popstate`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the encoded view on browser Back/Forward.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onPopState = (event: PopStateEvent): void => {
      const restored = stateToRoute(event.state, window.location.pathname);
      setRoute(restored);
      applyDocumentTitle(restored);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: Route): void => {
    setRoute((current) => {
      if (current.view === next.view && current.id === next.id) {
        return current;
      }
      if (typeof window !== 'undefined') {
        window.history.pushState(routeToState(next), '', routeToPath(next));
      }
      saveRoute(next);
      applyDocumentTitle(next);
      return next;
    });
  }, []);

  return { route, navigate };
}
